import Layout from "../components/site/Layout";
import { PageHeader, SectionLabel, SectionHeading } from "../components/site/Primitives";
import NextStep from "../components/site/NextStep";

const CREDS = [
  {
    badge: "CFF\u00ae",
    title: "Certified Financial Fiduciary",
    org: "National Association of Financial Fiduciaries",
  },
  {
    badge: "NSSA\u00ae",
    title: "National Social Security Advisor",
    org: "National Social Security Association",
  },
  {
    badge: "IRMAACP\u00ae",
    title: "IRMAA Certified Planner",
    org: "IRMAA Certified Planner Association",
  },
];

const NETWORK = [
  "CPAs, accountants, and tax preparers for annual return coordination and tax projection modeling",
  "Tax attorneys for LLC formation, operating agreement drafting, and legal opinion letters",
  "Independent business valuation specialists for IRA-LLC interest appraisals",
  "Trustees and trust counsel for beneficiary planning and SECURE Act strategies",
  "Estate planning specialists and attorneys for beneficiary designation and inheritance tax planning",
  "Financial advisors and RIAs for investment coordination within the tax strategy",
];

const FACTS = [
  { k: "Founded", v: "Boutique Family Office" },
  { k: "Headquarters", v: "McKinney, TX" },
  { k: "Service Area", v: "National" },
  { k: "Focus", v: "Retirement Tax Strategy" },
];

function FirmSection() {
  return (
    <section className="py-20 lg:py-28 bg-cream-alt" data-testid="about-firm">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-6 text-charcoal-teal leading-relaxed">
          <p>
            Retirement Tax Consultants is a boutique family office
            specializing exclusively in retirement tax strategy for affluent
            pre-retirees and retirees. The firm was founded on a single
            observation: the clients most at risk from retirement taxation
            — those with high income or large traditional IRA and 401(k)
            balances — are typically the least well-served by generalist
            financial and tax professionals.
          </p>
          <p>
            We operate as the strategic bridge between clients&rsquo; CPAs and
            financial advisors, providing the proactive, forward-looking
            retirement tax planning that neither role typically covers. For
            clients with complex situations, we coordinate a full family
            office team — including CPAs and accountants, tax attorneys,
            estate planning specialists, business valuation specialists,
            trustees, and other financial professionals — managed by RTC as
            a single engagement.
          </p>
        </div>
        <aside className="lg:col-span-5 bg-pale-green/40 p-8 lg:p-10 self-start">
          <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-6">
            At a Glance
          </div>
          <dl className="divide-y divide-sage-soft">
            {FACTS.map((f) => (
              <div key={f.k} className="flex items-center justify-between py-4">
                <dt className="text-sm text-charcoal-teal/70">{f.k}</dt>
                <dd className="font-serif text-deep-teal text-lg text-right">
                  {f.v}
                </dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>
    </section>
  );
}

function CredentialsBlock() {
  return (
    <div className="mt-12 grid sm:grid-cols-3 gap-px bg-sage/30 border border-sage/30">
      {CREDS.map((c) => (
        <div key={c.badge} className="bg-cream p-7 lg:p-8">
          <div className="font-serif text-3xl text-accent-gold">{c.badge}</div>
          <div className="mt-4 font-serif text-deep-teal text-xl leading-snug">
            {c.title}
          </div>
          <div className="mt-3 text-[10px] tracking-overline uppercase font-semibold text-charcoal-teal/60">
            {c.org}
          </div>
        </div>
      ))}
    </div>
  );
}

function FounderSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="about-founder">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-overline uppercase font-semibold text-accent-gold mb-6">
          Founder & President
        </div>
        <h2
          className="font-serif font-light text-deep-teal leading-[1.04] tracking-tight"
          style={{ fontSize: "clamp(2.5rem, 5vw, 4.25rem)" }}
        >
          David Hyden
        </h2>
        <p className="mt-4 text-accent-gold text-base tracking-wide font-medium">
          CFF&reg; · NSSA&reg; · IRMAACP&reg;
        </p>

        <CredentialsBlock />

        <div className="mt-14 max-w-3xl space-y-6 text-charcoal-teal leading-relaxed">
          <p>
            David Hyden founded Retirement Tax Consultants to serve the
            clients he saw being underserved at the intersection of tax
            planning and retirement income: affluent pre-retirees and
            retirees with high income or large traditional retirement
            account balances who needed more than a generalist CPA or
            financial advisor could provide.
          </p>
          <p>
            With certifications as a Certified Financial Fiduciary
            (CFF&reg;), National Social Security Advisor (NSSA&reg;), and
            IRMAA Certified Planner (IRMAACP&reg;), David brings a
            credential base that spans retirement income, Social Security
            optimization, Medicare planning, and tax strategy — the full
            picture of what retirement tax risk looks like in practice.
          </p>
          <p>
            David has developed proprietary frameworks and implementation
            resources including a comprehensive Retirement Tax Risk
            Analysis, a six-area diagnostic model covering the core risk
            exposures facing affluent retirees, and an advanced IRA-LLC
            conversion methodology with supporting legal analysis and
            compliance documentation.
          </p>
          <p>
            He works in partnership with a network of CPAs, accountants,
            tax attorneys, estate planning specialists, business valuation
            specialists, and financial advisors. Retirement Tax Consultants
            is based in McKinney, Texas, and serves clients nationally.
          </p>
        </div>
      </div>
    </section>
  );
}

function NetworkSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="about-network">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="Network" label="Our Professional Network" />
        <SectionHeading italicTail="managed as a single engagement.">
          A coordinated team —
        </SectionHeading>
        <p className="mt-6 text-charcoal-teal max-w-3xl leading-relaxed">
          For clients whose situations require it, RTC coordinates a curated
          network of professionals across disciplines.
        </p>

        <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-sage/30 border border-sage/30">
          {NETWORK.map((n, i) => (
            <li key={n} className="bg-cream p-7 lg:p-8 flex gap-5">
              <span className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold pt-1 whitespace-nowrap">
                {String(i + 1).padStart(2, "0")} / 06
              </span>
              <span className="text-deep-teal text-sm leading-relaxed">{n}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <Layout pageName="about">
      <PageHeader
        eyebrow="About the Firm"
        title="A boutique family office for"
        italicTail="retirement tax strategy."
        intro="The clients most at risk from retirement taxation are typically the least well-served by generalist financial and tax professionals. We were built to fix that."
      />
      <FirmSection />
      <FounderSection />
      <NetworkSection />
      <NextStep />
    </Layout>
  );
}
